import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Mail, KeyRound, Pencil, Trash2, Save, X } from "lucide-react";
import { useOperatorApi } from "@/hooks/useOperatorApi";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type DetailTab = "profil" | "services" | "trust";

export default function OperatorProviderDetail() {
  const { t } = useTranslation();
  const { providerId } = useParams();
  const { operatorFetch } = useOperatorApi();
  const navigate = useLocaleNavigate();
  const [provider, setProvider] = useState<any>(null);
  const [payment, setPayment] = useState<any>(null);
  const [auditLog, setAuditLog] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [stacks, setStacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitValue, setLimitValue] = useState<number>(20);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const [activeTab, setActiveTab] = useState<DetailTab>("profil");

  useEffect(() => { if (providerId) loadProvider(); }, [providerId]);

  async function loadProvider() {
    setLoading(true);
    const [provRes, paymentRes, auditRes, catsRes, stacksRes] = await Promise.all([
      supabase.from("provider_profiles").select("*, provider_billing_accounts(*)").eq("id", providerId!).single(),
      supabase.from("provider_mollie_payments").select("*").eq("provider_id", providerId!).maybeSingle(),
      supabase.from("audit_log").select("*").eq("entity_type", "PROVIDER").eq("entity_id", providerId!).order("created_at", { ascending: false }).limit(20),
      supabase.from("provider_categories").select("category").eq("provider_id", providerId!),
      supabase.from("provider_stacks").select("*, stack_tags(name, category)").eq("provider_id", providerId!),
    ]);
    setProvider(provRes.data);
    setPayment(paymentRes.data);
    setAuditLog(auditRes.data || []);
    setCategories(catsRes.data || []);
    setStacks(stacksRes.data || []);
    setLoading(false);
  }

  async function doAction(action: string, body?: any) {
    setActionLoading(action);
    try {
      await operatorFetch(`providers/${providerId}/${action}`, { method: "POST", body: body || { reason: action } });
      toast.success(t("op.pdActionSuccess", { action }));
      loadProvider();
    } catch (err: any) { toast.error(err.message); } finally { setActionLoading(null); }
  }

  async function saveMonthlyLimit() {
    setActionLoading("limit");
    try {
      await operatorFetch(`providers/${providerId}/limits`, { method: "PATCH", body: { monthly_limit: limitValue, reason: t("op.pdLimitSet", { limit: limitValue }) } });
      toast.success(t("op.pdLimitSet", { limit: limitValue }));
      setEditingLimit(false);
      loadProvider();
    } catch (err: any) { toast.error(err.message); } finally { setActionLoading(null); }
  }

  async function updatePaymentStatus(hasPayment: boolean) {
    setActionLoading("payment");
    try {
      await supabase.from("provider_mollie_payments").upsert({ provider_id: providerId!, has_payment_method: hasPayment, setup_status: hasPayment ? "active" : "none" }, { onConflict: "provider_id" });
      await supabase.from("provider_profiles").update({ has_payment_method: hasPayment }).eq("id", providerId!);
      toast.success(t("op.pdPaymentUpdated"));
      loadProvider();
    } catch (err: any) { toast.error(err.message); } finally { setActionLoading(null); }
  }

  function startEdit() {
    setEditData({
      company_name: provider.company_name || "",
      legal_name: provider.legal_name || "",
      contact_name: provider.contact_name || "",
      contact_role: provider.contact_role || "",
      contact_email: provider.contact_email || "",
      phone: provider.phone || "",
      website: provider.website || "",
      address_line1: provider.address_line1 || "",
      postal_code: provider.postal_code || "",
      city: provider.city || "",
      country: provider.country || "DE",
      vat_id: provider.vat_id || "",
      region: provider.region || "",
      geschaeftsfuehrer: provider.geschaeftsfuehrer || "",
      gruendungsjahr: provider.gruendungsjahr || "",
      bonitaet_rating: provider.bonitaet_rating || "",
      remote_supported: provider.remote_supported || false,
      sla_response_hours: provider.sla_response_hours || 24,
      monthly_opportunity_limit: provider.monthly_opportunity_limit || 20,
    });
    setEditMode(true);
  }

  async function saveEdit() {
    setActionLoading("save");
    try {
      const body = { ...editData };
      if (body.gruendungsjahr) body.gruendungsjahr = Number(body.gruendungsjahr);
      else delete body.gruendungsjahr;
      await operatorFetch(`providers/${providerId}`, { method: "PATCH", body });
      toast.success("Änderungen gespeichert");
      setEditMode(false);
      loadProvider();
    } catch (err: any) { toast.error(err.message); } finally { setActionLoading(null); }
  }

  if (loading) return <OperatorLayout title={t("op.colProvider")}><p className="text-caption">{t("common.loading")}</p></OperatorLayout>;
  if (!provider) return <OperatorLayout title={t("op.colProvider")}><p className="text-caption">{t("op.noProviders")}</p></OperatorLayout>;

  const CATEGORY_LABELS: Record<string, string> = {
    managed_service: t("op.pdCatManagedService"), project: t("op.pdCatProject"), security: t("op.pdCatSecurity"),
    cloud: t("op.pdCatCloud"), support: t("op.pdCatSupport"),
  };

  const stacksByCategory: Record<string, any[]> = {};
  stacks.forEach((s) => {
    const cat = s.stack_tags?.category || "Sonstige";
    if (!stacksByCategory[cat]) stacksByCategory[cat] = [];
    stacksByCategory[cat].push(s);
  });

  return (
    <OperatorLayout title={provider.company_name} subtitle={`${t("op.colProvider")} #${provider.id.slice(0, 8)}`}>
      <div className="flex border-b border-border mb-8">
        {([
          { id: "profil" as DetailTab, label: t("op.pdTabProfile") },
          { id: "services" as DetailTab, label: t("op.pdTabServices") },
          { id: "trust" as DetailTab, label: t("op.pdTrustKPIs") },
        ]).map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={cn("px-5 py-3 text-sm transition-colors border-b-2 -mb-px",
              activeTab === tab.id ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "profil" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-subheadline">{t("op.pdMasterData")}</h3>
                {!editMode ? (
                  <Button variant="quiet" size="sm" onClick={startEdit}><Pencil className="h-3.5 w-3.5 mr-1.5" />Bearbeiten</Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="commit" size="sm" onClick={saveEdit} disabled={actionLoading === "save"}><Save className="h-3.5 w-3.5 mr-1.5" />Speichern</Button>
                    <Button variant="quiet" size="sm" onClick={() => setEditMode(false)}><X className="h-3.5 w-3.5 mr-1.5" />Abbrechen</Button>
                  </div>
                )}
              </div>

              {editMode ? (
                <div className="grid grid-cols-2 gap-4">
                  {([
                    ["company_name", "Unternehmen", "text"],
                    ["legal_name", "Rechtlicher Name", "text"],
                    ["contact_name", "Kontaktperson", "text"],
                    ["contact_role", "Rolle", "text"],
                    ["contact_email", "E-Mail", "email"],
                    ["phone", "Telefon", "text"],
                    ["website", "Website", "text"],
                    ["geschaeftsfuehrer", "Geschäftsführer", "text"],
                    ["gruendungsjahr", "Gegründet (Jahr)", "number"],
                    ["address_line1", "Straße", "text"],
                    ["postal_code", "PLZ", "text"],
                    ["city", "Stadt", "text"],
                    ["vat_id", "USt-IdNr.", "text"],
                    ["region", "Region", "text"],
                    ["monthly_opportunity_limit", "Monatslimit", "number"],
                    ["sla_response_hours", "SLA (Stunden)", "number"],
                  ] as [string, string, string][]).map(([field, label, type]) => (
                    <div key={field}>
                      <label className="text-micro block mb-1">{label}</label>
                      <input type={type} value={editData[field] ?? ""} onChange={(e) => setEditData(d => ({ ...d, [field]: e.target.value }))}
                        className="w-full h-9 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary" />
                    </div>
                  ))}
                  <div>
                    <label className="text-micro block mb-1">Bonität</label>
                    <select value={editData.bonitaet_rating ?? ""} onChange={(e) => setEditData(d => ({ ...d, bonitaet_rating: e.target.value }))}
                      className="w-full h-9 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary">
                      <option value="">– Nicht bewertet –</option>
                      <option value="excellent">Ausgezeichnet</option>
                      <option value="good">Gut</option>
                      <option value="moderate">Moderat</option>
                      <option value="poor">Mangelhaft</option>
                      <option value="unknown">Unbekannt</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-3 cursor-pointer pb-2">
                      <input type="checkbox" checked={!!editData.remote_supported} onChange={(e) => setEditData(d => ({ ...d, remote_supported: e.target.checked }))} className="w-4 h-4 accent-accent" />
                      <span className="text-sm">Remote möglich</span>
                    </label>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      [t("op.pdContact"), provider.contact_name],
                      [t("common.email"), provider.contact_email || "—"],
                      [t("op.pdPhone"), provider.phone || "—"],
                      [t("op.pdWebsite"), provider.website || "—"],
                      [t("op.pdCompanyType"), provider.company_type || "—"],
                      ["Geschäftsführer", provider.geschaeftsfuehrer || "—"],
                      ["Gegründet", provider.gruendungsjahr ? `${provider.gruendungsjahr}` : "—"],
                      ["Bonität", provider.bonitaet_rating ? ({ excellent: "Ausgezeichnet", good: "Gut", moderate: "Moderat", poor: "Mangelhaft", unknown: "Unbekannt" } as Record<string, string>)[provider.bonitaet_rating] || provider.bonitaet_rating : "—"],
                      [t("op.colRegion"), provider.region || "—"],
                      [t("op.pdRemote"), provider.remote_supported ? t("common.yes") : t("common.no")],
                      [t("op.pdSLA"), provider.sla_tier || `${provider.sla_response_hours}h`],
                      [t("op.pdMonthlyLimit"), "__EDITABLE_LIMIT__"],
                    ].map(([label, value]) => (
                      <div key={label as string}>
                        <span className="text-micro block mb-1">{label}</span>
                        {value === "__EDITABLE_LIMIT__" ? (
                          editingLimit ? (
                            <div className="flex items-center gap-2">
                              <input type="number" min={1} max={999} value={limitValue} onChange={(e) => setLimitValue(Number(e.target.value))}
                                className="w-20 h-8 px-2 text-sm border border-border bg-background focus:outline-none focus:border-primary" />
                              <Button variant="commit" size="sm" onClick={saveMonthlyLimit} disabled={actionLoading === "limit"}>OK</Button>
                              <Button variant="quiet" size="sm" onClick={() => setEditingLimit(false)}>✕</Button>
                            </div>
                          ) : (
                            <button className="flex items-center gap-1.5 text-sm hover:text-accent transition-colors"
                              onClick={() => { setLimitValue(provider.monthly_opportunity_limit); setEditingLimit(true); }}>
                              {provider.monthly_opportunity_limit}
                              <Pencil className="h-3 w-3 text-muted-foreground" />
                            </button>
                          )
                        ) : (
                          <p className="text-sm">{value}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  {(provider.address_line1 || provider.city) && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <span className="text-micro block mb-1">{t("op.pdAddress")}</span>
                      <p className="text-sm">{[provider.address_line1, [provider.postal_code, provider.city].filter(Boolean).join(" "), provider.country].filter(Boolean).join(", ")}</p>
                    </div>
                  )}
                </>
              )}

              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                <Button variant="quiet" size="sm" onClick={() => doAction("resend-welcome")} disabled={!!actionLoading}>
                  <Mail className="h-3.5 w-3.5 mr-1.5" />{t("op.pdResendWelcome")}
                </Button>
                <Button variant="quiet" size="sm" onClick={() => doAction("reset-password")} disabled={!!actionLoading}>
                  <KeyRound className="h-3.5 w-3.5 mr-1.5" />{t("op.pdResetPassword")}
                </Button>
              </div>
            </div>

            {Array.isArray(provider.contacts) && provider.contacts.length > 0 && (
              <div className="border border-border bg-card p-6">
                <h3 className="text-subheadline mb-4">{t("op.pdAdditionalContacts")}</h3>
                <div className="space-y-3">
                  {provider.contacts.map((c: any, i: number) => (
                    <div key={c.id || i} className="border border-border p-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-micro block mb-1">{t("common.name")}</span>
                          <p className="text-sm">{c.name}</p>
                          {c.role && <p className="text-xs text-muted-foreground">{c.role}</p>}
                        </div>
                        {c.email && <div><span className="text-micro block mb-1">{t("common.email")}</span><p className="text-sm">{c.email}</p></div>}
                        {c.phone && <div><span className="text-micro block mb-1">{t("common.phone")}</span><p className="text-sm">{c.phone}</p></div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="border border-border bg-card p-6">
              <h3 className="text-subheadline mb-4">{t("op.pdStatusActions")}</h3>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div>
                  <span className="text-micro block mb-1">{t("op.pdApproved")}</span>
                  <span className={cn("text-sm font-medium", provider.is_approved ? "text-accent" : "text-muted-foreground")}>
                    {provider.is_approved ? t("common.yes") : t("common.no")}
                  </span>
                </div>
                <div>
                  <span className="text-micro block mb-1">{t("op.colBilling")}</span>
                  <span className="text-sm">{provider.billing_status || "—"}</span>
                </div>
                <div>
                  <span className="text-micro block mb-1">{t("op.pdSuspended")}</span>
                  <span className={cn("text-sm", provider.is_suspended && "text-destructive font-medium")}>
                    {provider.is_suspended ? t("common.yes") : t("common.no")}
                  </span>
                </div>
              </div>
              {provider.is_suspended && provider.suspended_reason && (
                <div className="bg-destructive/10 border border-destructive/30 p-3 mb-4">
                  <p className="text-xs font-medium text-destructive">{t("op.pdSuspendReason")} {provider.suspended_reason}</p>
                  {provider.suspended_at && <p className="text-micro text-muted-foreground mt-1">{t("op.pdSuspendedAt")} {new Date(provider.suspended_at).toLocaleString("de-DE")}</p>}
                </div>
              )}
              {provider.provider_billing_accounts?.[0]?.failed_debit_count > 0 && (
                <div className="bg-destructive/5 border border-destructive/20 p-3 mb-4">
                  <p className="text-xs font-medium text-destructive">{t("op.pdFailedDebits")} {provider.provider_billing_accounts[0].failed_debit_count}/3</p>
                </div>
              )}
              <div className="flex gap-3 flex-wrap">
                {!provider.is_approved && (
                  <Button variant="commit" size="sm" onClick={() => doAction("approve")} disabled={!!actionLoading}>{t("op.pdApprove")}</Button>
                )}
                {!provider.is_suspended ? (
                  <Button variant="quiet" size="sm" onClick={() => {
                    const reason = prompt(t("op.pdSuspendPrompt"));
                    if (reason) doAction("suspend", { reason });
                  }} disabled={!!actionLoading}>{t("op.pdSuspend")}</Button>
                ) : (
                  <>
                    <Button variant="quiet" size="sm" onClick={() => doAction("unsuspend", { reason: t("op.pdUnsuspendReason") })} disabled={!!actionLoading}>{t("op.pdUnsuspend")}</Button>
                    {provider.suspended_reason && <span className="text-xs text-destructive ml-2">{t("op.pdSuspendReason")} {provider.suspended_reason}</span>}
                  </>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="quiet" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10" disabled={!!actionLoading}>
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />{t("op.pdDelete")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("op.pdDeleteTitle")}</AlertDialogTitle>
                      <AlertDialogDescription dangerouslySetInnerHTML={{ __html: t("op.pdDeleteDesc", { company: provider.company_name }) }} />
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={actionLoading === "delete"}
                        onClick={async () => {
                          setActionLoading("delete");
                          try {
                            await operatorFetch(`providers/${providerId}`, { method: "DELETE" });
                            toast.success(t("op.pdDeleted"));
                            navigate("/operator/providers");
                          } catch (err: any) { toast.error(err.message); } finally { setActionLoading(null); }
                        }}>
                        {actionLoading === "delete" ? t("common.loading") : t("op.regDeleteConfirm")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            <div className="border border-border bg-card p-6">
              <h3 className="text-subheadline mb-4">{t("op.pdPayment")}</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div><span className="text-micro block mb-1">{t("op.pdPaymentStatus")}</span>
                  <p className={cn("text-sm font-medium", payment?.has_payment_method ? "text-accent" : "text-muted-foreground")}>
                    {payment?.has_payment_method ? t("op.statusActive") : payment?.setup_status === "pending" ? t("op.statusPending") : "—"}
                  </p>
                </div>
                <div><span className="text-micro block mb-1">{t("op.pdSetupStatus")}</span><p className="text-sm">{payment?.setup_status || "none"}</p></div>
                <div><span className="text-micro block mb-1">{t("op.pdMollieCustomer")}</span><p className="text-sm font-mono text-muted-foreground">{payment?.mollie_customer_id || "—"}</p></div>
                <div><span className="text-micro block mb-1">{t("op.pdPaymentMethods")}</span><p className="text-sm">{payment?.selected_methods?.join(", ") || "—"}</p></div>
              </div>
              <div className="flex gap-2">
                <Button variant="commit" size="sm" onClick={() => updatePaymentStatus(true)} disabled={actionLoading === "payment"}>{t("op.pdActivateManual")}</Button>
                <Button variant="quiet" size="sm" onClick={() => updatePaymentStatus(false)} disabled={actionLoading === "payment"}>{t("op.pdDeactivateManual")}</Button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="border border-border bg-card p-6">
              <h3 className="text-subheadline mb-4">{t("op.pdAuditLog")}</h3>
              <div className="space-y-3">
                {auditLog.length === 0 ? (
                  <p className="text-caption">{t("op.pdNoAuditLog")}</p>
                ) : (
                  auditLog.map((entry) => (
                    <div key={entry.id} className="py-2 border-b border-border last:border-0">
                      <p className="text-xs font-medium">{entry.event_type}</p>
                      <p className="text-micro">{new Date(entry.created_at).toLocaleString("de-DE")}</p>
                      {entry.meta && Object.keys(entry.meta).length > 0 && (
                        <p className="text-micro text-muted-foreground mt-1">{JSON.stringify(entry.meta).slice(0, 100)}</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "services" && (
        <div className="space-y-6 max-w-3xl">
          <div className="border border-border bg-card p-6">
            <h3 className="text-subheadline mb-4">{t("op.pdCategories")}</h3>
            {categories.length === 0 ? (
              <p className="text-caption">{t("op.pdNoCategories")}</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <span key={c.category} className="px-3 py-1.5 text-sm bg-primary/10 text-primary border border-primary/20">
                    {CATEGORY_LABELS[c.category] || c.category}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="border border-border bg-card p-6">
            <h3 className="text-subheadline mb-4">{t("op.pdStack")}</h3>
            {stacks.length === 0 ? (
              <p className="text-caption">{t("op.pdNoStack")}</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(stacksByCategory).map(([category, tags]) => (
                  <div key={category}>
                    <p className="text-xs font-medium text-muted-foreground mb-2">{category}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map((tg) => (
                        <span key={tg.id} className="px-2.5 py-1 text-xs bg-accent/10 text-accent border border-accent/20">
                          {tg.stack_tags?.name || tg.stack_tag_id}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-border bg-card p-6">
            <h3 className="text-subheadline mb-4">{t("op.pdServiceSettings")}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><span className="text-micro block mb-1">{t("op.colRegion")}</span><p className="text-sm">{provider.region || "—"}</p></div>
              <div><span className="text-micro block mb-1">{t("op.pdRemote")}</span><p className="text-sm">{provider.remote_supported ? t("common.yes") : t("common.no")}</p></div>
              <div><span className="text-micro block mb-1">{t("op.pdSLA")}</span><p className="text-sm">{provider.sla_tier || "—"}</p></div>
              <div><span className="text-micro block mb-1">{t("pdExtra.slaResponseTime")}</span><p className="text-sm">{provider.sla_response_hours ? `${provider.sla_response_hours}h` : "—"}</p></div>
              <div><span className="text-micro block mb-1">{t("op.pdCompanyType")}</span><p className="text-sm">{provider.provider_type || "—"}</p></div>
              <div><span className="text-micro block mb-1">{t("op.pdMonthlyLimit")}</span><p className="text-sm">{provider.monthly_opportunity_limit} {t("common.projectInquiries")} / {t("op.monthJan").split(" ")[0] ? t("op.billingMonth") : ""}</p></div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "trust" && (
        <div className="space-y-6 max-w-3xl">
          {/* Trust Level Management */}
          <div className="border border-border bg-card p-6">
            <h3 className="text-subheadline mb-4">{t("op.pdTrustProfile")}</h3>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-accent/10 border-2 border-accent flex items-center justify-center">
                <span className="text-2xl font-bold text-accent">{provider.trust_level ?? 0}</span>
              </div>
              <div>
                <p className="font-medium">{t("op.pdTrustLevel", { level: provider.trust_level ?? 0 })}</p>
                <p className="text-sm text-muted-foreground">{t("op.pdTrustScore", { score: Math.round(Number(provider.trust_score) || 0) })}</p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {[
                { key: "trust_gewerbeanmeldung", label: t("op.pdTrustGewerbeanmeldung"), level: 1 },
                { key: "trust_versicherung", label: t("op.pdTrustVersicherung"), level: 2 },
                { key: "trust_bonitaet", label: t("op.pdTrustBonitaet"), level: 3 },
                { key: "trust_identitaet", label: t("op.pdTrustIdentitaet"), level: 3 },
              ].map(item => (
                <label key={item.key} className="flex items-center gap-3 p-3 border border-border hover:bg-secondary/20 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!provider[item.key]}
                    onChange={async (e) => {
                      setActionLoading(item.key);
                      try {
                        await supabase.from("provider_profiles").update({
                          [item.key]: e.target.checked,
                          ...(e.target.checked ? { trust_verified_at: new Date().toISOString() } : {}),
                        }).eq("id", providerId!);
                        toast.success(e.target.checked ? t("op.pdTrustActivated", { label: item.label }) : t("op.pdTrustDeactivated", { label: item.label }));
                        loadProvider();
                      } catch (err: any) { toast.error(err.message); }
                      finally { setActionLoading(null); }
                    }}
                    className="w-4 h-4 accent-accent"
                    disabled={!!actionLoading}
                  />
                  <div>
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{t("op.pdTrustRequiredLevel", { level: item.level })}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Badges */}
            {provider.trust_badges?.length > 0 && (
              <div>
                <p className="text-micro mb-2">{t("op.pdActiveBadges")}</p>
                <div className="flex flex-wrap gap-2">
                  {(provider.trust_badges || []).map((badge: string) => {
                    const labels: Record<string, string> = {
                      verified_business: t("op.pdBadgeVerifiedBusiness"),
                      insured: t("op.pdBadgeInsured"),
                      premium_partner: t("op.pdBadgePremium"),
                      top_rated: t("op.pdBadgeTopRated"),
                      fast_response: t("op.pdBadgeFastResponse"),
                      high_acceptance: t("op.pdBadgeHighAcceptance"),
                    };
                    return (
                      <span key={badge} className="px-3 py-1.5 text-xs bg-accent/10 text-accent border border-accent/20">
                        {labels[badge] || badge}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* KPI Overview */}
          <div className="border border-border bg-card p-6">
            <h3 className="text-subheadline mb-4">{t("op.pdKPIs")}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-micro block mb-1">{t("op.pdLeadsReceived")}</span>
                <p className="text-lg font-medium">{provider.total_leads_received || 0}</p>
              </div>
              <div>
                <span className="text-micro block mb-1">{t("op.pdLeadsWon")}</span>
                <p className="text-lg font-medium">{provider.total_leads_won || 0}</p>
              </div>
              <div>
                <span className="text-micro block mb-1">{t("op.pdConversionRate")}</span>
                <p className="text-lg font-medium">{Math.round((provider.conversion_rate || 0) * 100)}%</p>
              </div>
              <div>
                <span className="text-micro block mb-1">{t("op.pdDisputeRate")}</span>
                <p className={cn("text-lg font-medium", (provider.dispute_rate || 0) > 0.1 ? "text-destructive" : "")}>
                  {Math.round((provider.dispute_rate || 0) * 100)}%
                </p>
              </div>
              <div>
                <span className="text-micro block mb-1">{t("op.pdAcceptanceRate")}</span>
                <p className="text-lg font-medium">{Math.round((provider.acceptance_rate || 0) * 100)}%</p>
              </div>
              <div>
                <span className="text-micro block mb-1">{t("op.pdAvgResponseTime")}</span>
                <p className="text-lg font-medium">{provider.avg_response_time_hours || "—"}h</p>
              </div>
              <div>
                <span className="text-micro block mb-1">{t("op.pdBillingModel")}</span>
                <p className="text-lg font-medium">{provider.billing_model === "subscription" ? t("op.pdBillingModelSub") : t("op.pdBillingModelPPC")}</p>
              </div>
              <div>
                <span className="text-micro block mb-1">{t("op.pdAvgLeadScore")}</span>
                <p className="text-lg font-medium">{Math.round(Number(provider.avg_lead_score) || 0)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </OperatorLayout>
  );
}
